import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import Internship from '../components/Internship';
import { CheckCircle, Briefcase, Users, FileText, Globe, GraduationCap } from 'lucide-react';

const InternshipPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const seoTitle = "Cybersecurity Internship Indore | 100% Job Assistance | Ciqura Labs";
  const seoDescription = "Join Ciqura Labs' Training + Internship program in Indore. Get real-world SOC exposure, 100% job assistance, mock interviews, and resume building with placement paths to Ciqur24 LLP Bengaluru.";
  const pageUrl = "https://ciquralabs.ciqur24.com/internship";

  return (
    <div style={{ background: 'var(--bg-primary)', minHeight: '100vh', paddingTop: '80px' }}>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <meta name="keywords" content="Cybersecurity internship Indore, Paid internship Bengaluru, 100% placement assistance Indore, Mock interview preparation, Resume building for IT, SOC Analyst internship, Ciqur24 LLP Bengaluru careers, Python automation developer internship" />
        <link rel="canonical" href={pageUrl} />

        {/* 🔹 ENHANCED JOB SCHEMA FOR SEARCH ENGINE RICH VISIBILITY */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "JobPosting",
            "title": "Cybersecurity & SOC Engineering Intern",
            "description": "Industrial Training + Active Corporate Internship program with 100% job assistance, mock technical interviews, and portfolio resume building workshops in Indore with routing to our Bengaluru headquarters.",
            "employmentType": "INTERN",
            "hiringOrganization": {
              "@type": "Organization",
              "name": "Ciqur24 LLP",
              "sameAs": "https://ciquralabs.ciqur24.com",
              "logo": "https://res.cloudinary.com/dlz8euavh/image/upload/v1769179291/3_zbjtyy.png"
            },
            "jobLocation": [
              {
                "@type": "Place",
                "address": {
                  "@type": "PostalAddress",
                  "streetAddress": "113, Shagun Arcade, Vijay Nagar",
                  "addressLocality": "Indore",
                  "addressRegion": "MP",
                  "postalCode": "452010",
                  "addressCountry": "IN"
                }
              }
            ],
            "baseSalary": {
              "@type": "MonetaryAmount",
              "currency": "INR",
              "value": "Competitive Stipend + Full Time Placement Support"
            }
          })}
        </script>
      </Helmet>

      {/* Hero Header Segment */}
      <section style={{ padding: '80px 7.6923%', textAlign: 'center', background: 'radial-gradient(circle at top, rgba(0,255,209,0.1) 0%, transparent 70%)' }}>
        <div style={{ display: 'inline-block', background: 'rgba(63, 255, 224, 0.1)', color: 'var(--brand-primary)', padding: '8px 20px', borderRadius: '30px', fontSize: '14px', fontWeight: '700', marginBottom: '24px', border: '1px solid var(--brand-primary)' }}>
          INDORE'S DEDICATED TRAINING-LINKED INTERNSHIP PLATFORM
        </div>
        <h1 className="display-huge" style={{ marginBottom: '24px' }}>
          Technical Training + <span style={{ color: 'var(--brand-primary)' }}>Corporate Internship Verification</span>
        </h1>
        <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '950px', margin: '0 auto', fontSize: '18px', lineHeight: 1.7 }}>
          Bridge the gap from student to enterprise specialist. Our program combines **Industrial Training inside our Indore branch lab** with an official **Corporate Internship pipeline routing to our Bengaluru headquarters**, fully managed by **Ciqur24 LLP**.
        </p>
      </section>

      {/* Comprehensive Program Breakdown (Visible Content Architecture) */}
      <section style={{ padding: '40px 7.6923%', color: 'var(--text-secondary)', lineHeight: '1.7' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(450px, 1fr))', gap: '40px' }} className="responsive-grid">
          <div>
            <h2 className="heading-1" style={{ color: '#fff', marginBottom: '16px' }}>How Our Placement Blueprint Operates</h2>
            <p className="body-medium" style={{ marginBottom: '12px' }}>
              Finding technical roles requires proven, hands-on experience. At Ciqura Labs, our internship track is explicitly built to target current hiring parameters. We begin with rigorous daily laboratory deep-dives covering complex tracking topologies, software development constraints, and network defense strategies.
            </p>
            <p className="body-medium">
              Once you master these fundamentals, you transition directly into simulated internship tasks. You will manage incoming security tickets, refactor live software codebases, troubleshoot configuration blocks, and build technical assets that demonstrate real-world competence to prospective employers.
            </p>
          </div>
          <div>
            <h2 className="heading-1" style={{ color: '#fff', marginBottom: '16px' }}>Direct Link to the Bengaluru IT Marketplace</h2>
            <p className="body-medium" style={{ marginBottom: '12px' }}>
              Because Ciqura Labs operates as an integrated training arm of **Ciqur24 LLP Bengaluru**, our students bypass standard entry-level application queues. Top performers within our local Vijay Nagar classes gain direct eligibility for Pre-Placement Offers (PPOs) and dedicated internship allocations inside our primary delivery offices.
            </p>
            <p className="body-medium">
              This pipeline ensures you don't just finish with a simple training certificate. Instead, you exit with validated, professional tenure backed by an active cybersecurity firm operating inside India's premier tech capital.
            </p>
          </div>
        </div>
      </section>

      {/* USP Feature Grid */}
      <section style={{ padding: '60px 7.6923%', background: 'var(--bg-secondary)' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '24px' }}>
            <USPCard 
              icon={<CheckCircle size={32} />} 
              title="100% Dedicated Placement Framework" 
              desc="Our local placement office stays connected with expanding technology centers across Bengaluru, Mumbai, and Gandhinagar to secure active interview invites for our students." 
            />
            <USPCard 
              icon={<Users size={32} />} 
              title="Technical & HR Mock Drills" 
              desc="Participate in live mock technical evaluations and behavioral reviews conducted directly by operational engineers from Ciqur24 LLP." 
            />
            <USPCard 
              icon={<FileText size={32} />} 
              title="ATS-Optimized Portfolio Building" 
              desc="We help refactor your professional profile and resume layout, framing your laboratory project logs to clear strict corporate recruitment filters." 
            />
            <USPCard 
              icon={<Briefcase size={32} />} 
              title="Direct Path to Ciqur24 HQ" 
              desc="Eligible graduates receive direct access to internship tracks, site integration, and permanent project engineering roles inside our parent company." 
            />
          </div>
        </div>
      </section>

      <div style={{ padding: '40px 0' }}>
        <Internship />
      </div>
    </div>
  );
};

const USPCard = ({ icon, title, desc }) => (
  <div style={{ padding: '32px', background: 'var(--bg-primary)', border: '1px solid var(--border-subtle)', borderRadius: '16px' }}>
    <div style={{ color: 'var(--brand-primary)', marginBottom: '20px' }}>{icon}</div>
    <h3 className="heading-3" style={{ marginBottom: '12px' }}>{title}</h3>
    <p className="body-medium" style={{ color: 'var(--text-secondary)', lineHeight: 1.6 }}>{desc}</p>
  </div>
);

export default InternshipPage;